import { ActionBar } from '../action-bar';
import { MaterialTable } from '../material-table';
import { Typography } from '../typography';
import { viewTypes } from '../../constants';
import { MRT_ColumnOrderState, MRT_SortingState } from "material-react-table";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./custom-forms.scss";
import { useAuth } from '@erpsquad/common/runtime';
import { Footer } from '../footer/footer';
import { IPaginationModel } from '../../types';
import { Box } from "@mui/material";
import GridCard from "./grid-view/grid-view.js";
import { useAppTranslations } from "../../hooks/use-translations";
import { useTranslation } from 'react-i18next';
import useApi from '../../hooks/useApi';
import { getV1FormBuilder, setBaseUrl as setSystemBaseUrl } from '../../api-client/api.system-feature/api';
import { getToken } from '../../utils/common';

interface ActionData {
  label: string;
  position: number;
}

interface ModuleConfig {
  filterName: string;
  redirectionPath: string;
}



const INITIAL_ORDER = ["name", "fields", "noOfCustomFields", "customFields"];
const DEFAULT_ACTION_BAR_DATA = [{ label: 'Table', position: 0 }];
const CUSTOM_FIELD_DISPLAY_LIMIT = 5;

interface FormBuilderState {
  rows: any[];
  isLoading: boolean;
  paginationModel: {
    pageNo: number;
    limit: number;
    totalCount: number;
  };
}

const Forms = ({ config }: { config?: any }) => {
  const { t } = useTranslation();
  useAppTranslations(); // Load translations for current module
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { callApi } = useApi();

  const [formBuilder, setFormBuilder] = useState<FormBuilderState>({
    rows: [],
    isLoading: false,
    paginationModel: {
      pageNo: 1,
      limit: 20,
      totalCount: 0
    }
  });

  // Initialize state with proper defaults and memoization
  const [actionBarData, setActionBarData] = useState<ActionData[]>(() => {
    const formView = localStorage.getItem("form-view");
    return formView ? JSON.parse(formView) : DEFAULT_ACTION_BAR_DATA;
  });

  const [search, setSearch] = useState<string>("");
  const [actionBarActiveTab, setActionBarActiveTab] = useState<string>(() =>
    actionBarData?.[0]?.label || viewTypes.TABLE
  );
  const [columnOrder, setColumnOrder] = useState(INITIAL_ORDER);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  // Memoized computed values to prevent unnecessary recalculations
  const currentModule = useMemo(() => location.pathname.split('/')[2], [location.pathname]);

  const modulesConfig = useMemo(() => ({
    ...config
  }), [config]);

  const moduleConfig = useMemo(() =>
    modulesConfig[currentModule] || { filterName: '', redirectionPath: '' },
    [currentModule, modulesConfig]
  );

  const navigationPath = useMemo(() =>
    `/dashboard/${currentModule}${moduleConfig.redirectionPath}`,
    [currentModule, moduleConfig.redirectionPath]
  );

  const filters = useMemo(() =>
    `(company_id.eq=${user?.company_id}&module.eq=${moduleConfig.filterName})`,
    [user?.company_id, moduleConfig.filterName]
  );

  const paginationData = useMemo(() => {
    const { pageNo, limit, totalCount } = formBuilder.paginationModel;
    const total = limit ? Math.ceil(totalCount / limit) : 0;
    const skip = limit * (pageNo - 1);
    return { total, skip, pageNo, limit };
  }, [formBuilder.paginationModel]);

  // Utility function moved outside render to prevent recreation
  const formatText = useCallback((key: string): string => {
    return key
      ? key
        .split(/[:_-]/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      : key;
  }, []);

  // Memoized event handlers to prevent child re-renders
  const handleColumnOrdering = useCallback((colOrder: MRT_ColumnOrderState) => {
    setColumnOrder(colOrder.slice(1));
  }, []);

  const handlePaginationModel = useCallback((model: IPaginationModel) => {
    setFormBuilder(prev => ({
      ...prev,
      paginationModel: {
        ...model,
        totalCount: prev.paginationModel.totalCount
      }
    }));
  }, []);

  const handleSearch = useCallback((searchValue: string) => {
    setSearch(searchValue);
  }, []);

  const handleRowClick = useCallback((rowData: any) => {
    navigate(navigationPath, {
      state: rowData,
    });
  }, [navigate, navigationPath]);

  // Memoized columns definition to prevent recreation on each render
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Form Name",
      visible: true,
      Cell: ({ row }: any) =>
        row?.original?.name ? (
          <div
            onClick={() => handleRowClick(row.original)}
            style={{ cursor: 'pointer' }}
          >
            {formatText(row.original.name)}
          </div>
        ) : (
          "-"
        ),
    },
    {
      accessorKey: "fields.length",
      header: "No. of Fields",
      visible: true,
      size: 150
    },
    {
      accessorKey: "fields",
      header: "No. of custom fields",
      visible: true,
      size: 80,
      Cell: ({ row }: any) =>
        row?.original?.fields ? (
          <div>
            {
              row.original.fields.filter(
                (field: { is_system_field: boolean }) => !field.is_system_field,
              ).length
            }
          </div>
        ) : (
          "-"
        ),
    },
    {
      accessorKey: "fields",
      header: "Custom Fields",
      visible: true,
      Cell: ({ row }: any) => {
        const fields = row?.original?.fields?.filter(
          (field: { is_system_field: boolean; label: string }) =>
            !field?.is_system_field && field?.label,
        ) || [];

        if (!fields.length) return '-';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', p: 0.5 }}>
            {fields.slice(0, CUSTOM_FIELD_DISPLAY_LIMIT).map((item: any, index: number) => (
              <Typography
                key={`${item.label}-${index}`}
                type="s5"
                weight="medium"
                className="forms--CustomField"
              >
                {item?.label?.includes(".") ? t(item.label) : item.label}
              </Typography>
            ))}
            {fields.length > CUSTOM_FIELD_DISPLAY_LIMIT && (
              <Typography
                type="s5"
                weight="medium"
                className="forms--CustomField"
              >
                +{fields.length - CUSTOM_FIELD_DISPLAY_LIMIT} more
              </Typography>
            )}
          </Box>
        );
      }
    },
  ], [formatText, handleRowClick, t]);

  // Memoized footer pages to prevent recreation
  const footerPages = useMemo(() => [
    {
      name: 'Default',
      _id: 1,
      data: []
    }
  ], []);


  const fetchForms = useCallback(async () => {
    if (!moduleConfig.filterName || !user?.company_id) return;

    const sort = sorting?.[0];
    const params = {
      skip: paginationData.skip,
      limit: paginationData.limit,
      search,
      filters,
      ...(sort && { order: `${sort.id}:${sort.desc ? -1 : 1}` }),
      ...getToken(),
      'x-timezone': getToken()['x-timezone']?.toString() || ''
    };

    setFormBuilder(prev => ({ ...prev, isLoading: true }));
    const response = await getV1FormBuilder({
      ...params,
      ...getToken
    })
    setFormBuilder({
      rows: response?.data?.form,
      isLoading: false,
      paginationModel: {
        ...formBuilder.paginationModel,
        ...response?.data?.pagination
      }
    });


  }, [filters,
    paginationData.skip,
    paginationData.limit,
    sorting,
    search,
    moduleConfig.filterName,
    user?.company_id,
  ])
  // Effect for fetching data - optimized with proper dependencies
  useEffect(() => {
    fetchForms()
  }, [
    fetchForms
  ]);

  // Effect for setting active tab - only run when actionBarData changes
  useEffect(() => {
    if (actionBarData?.length && actionBarData[0].label !== actionBarActiveTab) {
      setActionBarActiveTab(actionBarData[0].label);
    }
  }, [actionBarData]);

  // Effect for localStorage - debounced to prevent excessive writes
  useEffect(() => {
    if (actionBarData?.length) {
      const timeoutId = setTimeout(() => {
        localStorage.setItem("form-view", JSON.stringify(actionBarData));
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [actionBarData]);

  return (
    <section className="forms">
      <ActionBar
        title="Form"
        button={false}
        data={actionBarData}
        setData={setActionBarData}
        active={actionBarActiveTab}
        setActive={(tab) => {
          setActionBarActiveTab(tab);
        }}
        handleSearch={handleSearch}
        showFilter={false}
      />

      {actionBarActiveTab === viewTypes.TABLE && (
        <MaterialTable
          enablePagination={false}
          columnOrder={["mrt-row-select", ...columnOrder]}
          handleColumnOrdering={handleColumnOrdering}
          rows={formBuilder.rows}
          columns={columns}
          totalPages={1}
          paginationModel={{ pageNo: 1, limit: 20 }}
          enableEditing={false}
          states={{ sorting: sorting || [] }}
          onSortingChange={setSorting}
          disableDefaultActionColumn
          getSelectedData={() => { }}
        />
      )}

      {actionBarActiveTab === viewTypes.GRID && (
        <GridCard data={formBuilder.rows} config={config} />
      )}

      <Footer
        pages={footerPages}
        setPages={() => { }}
        currentPage={1}
        setCurrentPage={() => { }}
        total={paginationData.total}
        paginationModel={{
          pageNo: formBuilder.paginationModel.pageNo,
          limit: formBuilder.paginationModel.limit
        }}
        handlePaginationModel={handlePaginationModel}
        sharedPages={[]}
        resource=""
        savePageConfiguration={() => { }}
        handlePageDelete={() => { }}
        enablePages={false}
        isPaginationDisplayed={true}
      />
    </section>
  );
};

export default Forms;